import { ChevronLeft, ChevronRight, AlertTriangle, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtQty, MOVE_TYPES } from "@/lib/api";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sk-rise">
      <div>
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-slate-900 sm:text-5xl" data-testid={testid}>{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500 md:text-base">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function StockBadge({ stock, seuil, testid }) {
  let cls = "bg-emerald-50 text-emerald-700 border-emerald-200", label = "OK";
  if (stock <= 0) { cls = "bg-rose-50 text-rose-700 border-rose-200"; label = "Rupture"; }
  else if (stock < seuil) { cls = "bg-amber-50 text-amber-700 border-amber-200"; label = "Sous seuil"; }
  return <span data-testid={testid} className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

const TYPE_CLS = {
  entree: "bg-emerald-100 text-emerald-800",
  sortie: "bg-slate-200 text-slate-800",
  ajustement: "bg-blue-100 text-blue-800",
  sortie_kit: "bg-purple-100 text-purple-800",
};

export function TypeBadge({ type }) {
  return <span className={`inline-flex whitespace-nowrap rounded px-2 py-0.5 text-xs font-semibold ${TYPE_CLS[type] || ""}`}>{MOVE_TYPES[type] || type}</span>;
}

export function Changes({ changes }) {
  return (
    <div className="space-y-0.5 font-mono text-xs text-slate-600">
      {(changes || []).map((c) => (
        <div key={c.code}>{c.code} : {fmtQty(c.before)} → <span className="font-semibold text-slate-900">{fmtQty(c.after)}</span></div>
      ))}
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage, testid = "pager" }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between gap-4 border-t px-4 py-3 text-sm text-slate-600" data-testid={testid}>
      <span data-testid={`${testid}-info`}>{total} résultat(s) — page {page} / {pages}</span>
      <div className="flex gap-2">
        <Button variant="outline" className="h-11 w-11 p-0" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" className="h-11 w-11 p-0" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function IssuesAlert({ title, issues, testid }) {
  if (!issues?.length) return null;
  return (
    <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid={testid}>
      <div className="mb-2 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> {title}</div>
      <ul className="list-disc space-y-1 pl-5">{issues.map((i, k) => <li key={k}>{i}</li>)}</ul>
    </div>
  );
}

export function Panel({ title, icon: Icon, children, actions, testid, className = "" }) {
  return (
    <section className={`rounded-md border bg-white shadow-sm sk-rise ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-wide text-slate-900">{Icon && <Icon className="h-5 w-5 text-slate-500" />}{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function QtyStepper({ value, onChange, min = 0, step = 1, size = "lg", testid }) {
  const n = Number(value) || 0;
  const h = size === "sm" ? "h-11" : "h-14";
  return (
    <div className="flex items-center gap-1.5">
      <Button type="button" variant="outline" className={`${h} ${size === "sm" ? "w-11" : "w-14"} p-0`} onClick={() => onChange(Math.max(min, +(n - step).toFixed(3)))} data-testid={`${testid}-minus`}><Minus className="h-5 w-5" /></Button>
      <Input type="number" min={min} step="any" value={value} onChange={(e) => onChange(e.target.value)} className={`${h} ${size === "sm" ? "w-20 text-lg" : "w-28 text-2xl"} text-center font-mono font-bold`} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" className={`${h} ${size === "sm" ? "w-11" : "w-14"} p-0`} onClick={() => onChange(+(n + step).toFixed(3))} data-testid={`${testid}-plus`}><Plus className="h-5 w-5" /></Button>
    </div>
  );
}

export const TH = "text-xs font-semibold uppercase tracking-wider text-slate-500";
